import { Component, ElementRef, ViewChild, Input, Output, EventEmitter, HostListener, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatStreamFn, parseChatJson, streamChatOnce } from '../../../utils-lib/chat-stream';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import {TaxPlanningService} from "../../../tpiq-services/tax-planning.service";
import { KnolliChatService } from '../../../../app/tpiq-services/knolli-chat.service';
import { ChatMessage } from './rap-questionnaire';
import {Session} from "../../../services/Session";
import {LoadingService} from "../../../services/LoadingService";
import { environment } from "../../../../environments/environment";
import { MarkdownModule } from 'ngx-markdown';
import { trackPendoEvent } from '../../../utils-lib/pendo-track';
import {MatExpansionModule} from "@angular/material/expansion";
import { NumeralService } from '../../../services/Numeral.service';
import { PROVINCES } from '../../../constants/Provinces.constants';
import { WORKING_SET_CONTEXT_TYPE, buildDiscoveryWorkingSet, rankByDisplayedTop5 } from '../../../utils-lib/discovery-working-set';


declare const _: any;

/**
 * A working-set send that is in flight.
 * It lives outside the component, because Discovery is destroyed when the advisor leaves
 * and a new instance is created on return. The new instance finds the send here.
 */
interface WorkingSetSend {
  chatId: number;
  fingerprint: string;
  /** The 'Summarizing…' bubble. Every instance shows this one object. */
  message: ChatMessage;
  /** Settles when the stream ended and the fingerprint is saved. Never rejects. */
  promise: Promise<void>;
}

/** True when a chat history prompt is a Discovery working set. The set is a JSON object with this context type. */
function isWorkingSetPrompt(prompt: unknown): boolean {
  return !!parseWorkingSetPrompt(prompt);
}

/** The working-set object of a chat history prompt, or null when the prompt is not a working set. */
function parseWorkingSetPrompt(prompt: unknown): any {
  let value: any = prompt;
  // Two rounds: the text may be the JSON itself or a JSON string that holds it.
  for (let round = 0; round < 2 && typeof value === 'string'; round++) {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return !!value && typeof value === 'object' && value.contextType === WORKING_SET_CONTEXT_TYPE ? value : null;
}

/** The label of the Find More action. The action sends it as typed. */
// TODO(Jackie): copy not approved
export const FIND_MORE_PROMPT = 'Find More Strategies';

/** In-flight sends, keyed by the working-set storage key. An entry is removed when its send settles. */
const workingSetSends = new Map<string, WorkingSetSend>();

@Component({
  selector: 'app-rap-questionnaire',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MarkdownModule,
    MatExpansionModule
  ],
  templateUrl: './rap-questionnaire.component.html',
  styleUrls: ['./rap-questionnaire.component.scss']
})
export class RapQuestionnaireComponent implements OnChanges, OnDestroy {

  /**
   * Deadline for a chat stream, in milliseconds.
   *
   * The prod chat load balancer cuts an idle stream at 60s, and the slowest
   * answer measured over a week was 43s. 90s therefore sits above both, so
   * this timer only fires when the stream stalls with no error at all.
   */
  static readonly CHAT_TIMEOUT_MS = 90000;

  /**
   * Test seam for the chat stream. Specs replace it with a fake, so they can
   * drive the close, error, and timeout paths with no network. Leave it unset
   * in the app, and streamChatOnce uses the real fetch-event-source.
   */
  streamFn?: ChatStreamFn;

  @Input() ocrData: any;
  @Input() plan: any;
  @Input() chatId : any;
  @Input() documentId : any;
  @Input() questionnaireTrack : any;
  @Input() aiSuggestedStrategies: any;
  @Input() questionnaireUtilization: any;
  /** 
   * Flag to indicate if this is a RAP edit session
   * When true, chatId should be stored in RAP session payload, not database
   */
  @Input() isRapEditMode: boolean = false;
  /** 
   * Session ID for RAP mode (used for session-scoped localStorage key)
   * Prevents cross-tab leakage when same plan is edited in multiple tabs
   */
  @Input() sessionId?: string;
  org: string =  Session.getOrg();
  user: string = Session.getUser().id;
  userDetails = Session.getUser();
  copiedIndex: number | null = null;
  streamCompletedIndexes = new Set<number>();
  // Messages come from parent component
  @Input() messages: ChatMessage[] = [];
  @Output() messagesChange = new EventEmitter<ChatMessage[]>();
  @Output() nextClicked = new EventEmitter<string>();
  @Output() sectionChange = new EventEmitter<number>();
  /** 
   * Emits chatId changes to parent component
   * Parent stores chatId in RAP session payload for cross-device persistence
   */
  @Output() chatIdChange = new EventEmitter<number>();
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('chatInput') chatInput!: ElementRef;

  userInput = '';
  isStreaming = false;
  isLoading = false;
  aiAssistantId = environment.questionnaireAiAssistantId;
  aiResponse = '';
  chatApiUrl = environment.KNOLLI_CHAT_URL + "/api/public/ai-chat";
  private downX = 0;
  private downY = 0;
  engagementTracked = false;
  

  /**
   * Set when Discovery was entered before the OCR data or the strategies arrived.
   * ngOnChanges sends the list once they do.
   */
  private workingSetDeferred = false;

  /**
   * Set when pendingSummary() is called, which is the exit (End Conversation via Next).
   * A deferred send does not start after that, so it cannot meet the End Conversation stream.
   * A Restart clears it.
   */
  private exitStarted = false;

  /** The history load of a resumed chat, while it runs. ensureWorkingSetSent waits for it. */
  private historyLoad?: Promise<void>;

  /** True when the history of the current chat failed to load. Such a chat gets no list. */
  private chatHasHistory = false;

  /** The latest working set in the history of the current chat. Undefined when the history holds none. */
  private historyWorkingSet?: { fingerprint: string | null };

  private destroyed = false;

  /** Injects the services for plan data, the Knolli chat, loading state and amount formatting. */
  constructor(private taxPlanningService: TaxPlanningService, private knolliChatService: KnolliChatService, private loadingService: LoadingService, private numeralService: NumeralService) { }

  async ngOnInit() {
    // Discovery opens on the chat. The Top 5 list stays above it.
    await this.showAiQuestionnarieSection(false);
  }

  ngOnDestroy() {
    this.destroyed = true;
  }

  ngAfterViewInit() {
    this.focusChatInput();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['aiSuggestedStrategies']) {
      this.aiSuggestedStrategies = rankByDisplayedTop5(this.aiSuggestedStrategies);
    }
    // The list was held back on entry because its data was not there yet
    if (this.workingSetDeferred && !this.exitStarted && (changes['ocrData'] || changes['aiSuggestedStrategies'])) {
      this.ensureWorkingSetSent();
    }
  }
  
 userObj() {
    return {
      firstName: this.userDetails.first_name,
      lastName: this.userDetails.last_name,
      userEmail: this.userDetails.id,
      organisation: this.org
    };
  }

  getUserInitials(): string {
    const firstName = this.userDetails?.first_name?.trim() || '';
    const lastName = this.userDetails?.last_name?.trim() || '';

    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();

    return `${firstInitial}${lastInitial}`;
  }

  @HostListener('document:mousedown', ['$event'])
onMouseDown(event: MouseEvent) {
  const target = event.target as HTMLElement;

  if (
    this.chatInput?.nativeElement.contains(target) ||
    target.closest('.input-area') ||
    target.tagName === 'INPUT' ||       // ← add this
    target.tagName === 'TEXTAREA'       // ← and this
  ) {
    return;
  }

  this.downX = event.clientX;
  this.downY = event.clientY;
}

@HostListener('document:mouseup', ['$event'])
onMouseUp(event: MouseEvent) {
  const target = event.target as HTMLElement;

  if (
    this.chatInput?.nativeElement.contains(target) ||
    target.closest('.input-area') ||
    target.tagName === 'INPUT' ||       // ← add this
    target.tagName === 'TEXTAREA'       // ← and this
  ) {
    return;
  }

  const dx = Math.abs(event.clientX - this.downX);
  const dy = Math.abs(event.clientY - this.downY);

  if (dx < 3 && dy < 3) {
    window.getSelection()?.removeAllRanges();
  }
}
  
  initializeChatId() {
    if (!this.plan || !this.plan.id) {
      console.error("Plan ID is missing.");
      return;
    }

    // RAP MODE: Use session-scoped localStorage key and check RAP session payload
    if (this.isRapEditMode) {
      // 1. Check session-scoped localStorage (prevents cross-tab leakage)
      const sessionStorageKey = this.sessionId 
        ? `chatId_${this.plan.id}_${this.sessionId}` 
        : `chatId_${this.plan.id}`; 
      const savedChatKey = localStorage.getItem(sessionStorageKey);
      
      if (savedChatKey) {
        const parts = savedChatKey.split("_");
        if (parts.length >= 2) {
          this.chatId = Number(parts[parts.length - 1]); 
          return;
        }
      }
      
      // 2. If chatId is provided from parent (from RAP session payload), use it
      if (this.chatId) {
        // Store in session-scoped localStorage for immediate access
        const combinedKey = `${this.plan.id}_${this.chatId}`;
        localStorage.setItem(sessionStorageKey, combinedKey);
        return;
      }
      
      // 3. Create new chat session (lazy initialization - only when user sends first message)
      // Note: chatId will be stored in RAP session payload, not database
      this.createNewChatSessionForRap(sessionStorageKey);
      return;
    }

    // NORMAL MODE: Use plan-scoped localStorage and save to database
    const storageKey = `chatId_${this.plan.id}`;
    const savedChatKey = localStorage.getItem(storageKey);

    // Already saved chat for this plan
    if (savedChatKey) {
      const parts = savedChatKey.split("_");
      if (parts.length === 2) {
        this.chatId = Number(parts[1]);
      }
      return;
    }
    
    this.loadingService.triggerLoadingEvent(true);
    // New chat
    
    this.knolliChatService.getChatConfig(this.aiAssistantId, this.userObj()).subscribe({
      next: (res) => {
        this.loadingService.triggerLoadingEvent(false);
        if (res?.id) {
          this.chatId = res.id;
          let data = {
              fileId: this.plan.documentId? this.plan.documentId : this.documentId,
              chatId: this.chatId
            };
          // NORMAL MODE: Save to database (not in RAP mode)
          this.taxPlanningService.saveDRaftPlanChatId(this.org, this.user, data)
                .subscribe({
                  next: () => {},
                  error: (error) => {
                    this.loadingService.triggerLoadingEvent(false);
                  }
           });
          const combinedKey = `${this.plan.id}_${this.chatId}`;
          localStorage.setItem(storageKey, combinedKey);

          this.ensureWorkingSetSent();
        }
      },
      error: (err) => {
        console.error('Chat Config Error:', err)
        this.loadingService.triggerLoadingEvent(false);
      }
    });
  }

  /** State name of the plan home state. Uses the same country list as the wizard. */
  private homeStateName(): string {
    const country = this.userDetails?.orgCountryCode || 'US';
    const found = PROVINCES.COUNTRY_PROVINCES.find((state: any) =>
      state.country === country && state.short === this.plan?.homeState);
    return found ? found.name : '';
  }

  /** First prompt of the chat: the OCR data and all RAP strategies. */
  private buildDiscoveryContext(): any {
    return buildDiscoveryWorkingSet({
      ocrData: this.ocrData,
      planningYear: this.plan?.taxYear,
      strategies: this.aiSuggestedStrategies,
      stateName: this.homeStateName(),
      formatAmount: (amount: number) => this.numeralService.format('$0,0', amount)
    });
  }

  /**
   * Creates a new chat session for RAP mode
   * Does NOT save to database (maintains sandbox isolation)
   * Stores chatId in session-scoped localStorage and emits to parent for RAP session storage
   */
  private createNewChatSessionForRap(sessionStorageKey: string): void {
    this.loadingService.triggerLoadingEvent(true);
    this.knolliChatService.getChatConfig(this.aiAssistantId, this.userObj()).subscribe({
      next: (res) => {
        this.loadingService.triggerLoadingEvent(false);
        if (res?.id) {
          this.chatId = res.id;
          
          // RAP MODE: Store in session-scoped localStorage only
          // Do NOT save to database (maintains sandbox isolation)
          const combinedKey = `${this.plan.id}_${this.chatId}`;
          localStorage.setItem(sessionStorageKey, combinedKey);
          
          // Emit chatId to parent component so it can be stored in RAP session payload
          // Parent component will handle storing in rapSession.payload.chatId
          // This ensures cross-device persistence via rap_edit_drafts.json_payload
          this.chatIdChange.emit(this.chatId);
          
          this.ensureWorkingSetSent();
        }
      },
      error: (err) => {
        console.error('[RAP] Chat Config Error:', err);
        this.loadingService.triggerLoadingEvent(false);
      }
    });
  }

  /** localStorage key of the saved chat id. Same key the chat lookup in initializeChatId uses. */
  private chatStorageKey(): string {
    return this.isRapEditMode && this.sessionId
      ? `chatId_${this.plan.id}_${this.sessionId}`
      : `chatId_${this.plan.id}`;
  }

  /** localStorage key of the fingerprint of the list last sent to this chat. */
  private workingSetKey(): string {
    return `${this.chatStorageKey()}_workingSet`;
  }

  /** Fingerprint of the list: id, rank, plan amount and estimated savings of every strategy in the payload. */
  private workingSetFingerprint(context: any): string {
    return JSON.stringify(context.strategies.map((strategy: any) =>
      [strategy.strategyId, strategy.rank, strategy.planAmount, strategy.estimatedSavings ?? null]));
  }

  /** Fingerprint of the strategies in a working set from the history. Null when the set has no readable list. */
  private historyFingerprint(workingSet: any): string | null {
    return Array.isArray(workingSet?.strategies) ? this.workingSetFingerprint(workingSet) : null;
  }

  /**
   * Sends the Discovery list to the chat when the chat has not seen this list.
   * Call it on every Discovery entry. It sends once per list:
   * - a new chat always gets the list
   * - a resumed chat whose history has no working set gets it once, also a legacy chat with the raw OCR prompt
   * - a resumed chat whose latest working set holds the same list gets nothing
   * - a resumed chat whose latest working set holds another list gets it once, when the saved fingerprint differs too
   * A resumed chat loads its history first. The send waits for that load, so it cannot race the load.
   * When the load fails, nothing is sent.
   * A second call during the send does not send again, also from a new instance.
   * The new instance adopts the bubble of the send in flight when the chat id and the list are the same.
   * Nothing goes out until the OCR data and the strategies are loaded. ngOnChanges sends it when they arrive.
   * The reply streams into a 'Summarizing…' bubble. A failed send is not retried. The next entry sends again.
   * The fingerprint is saved only after a send succeeds.
   */
  ensureWorkingSetSent(): Promise<void> {
    if (!this.chatId || !this.plan?.id) return Promise.resolve();
    if (this.historyLoad) {
      return this.historyLoad.then(() => this.destroyed ? undefined : this.ensureWorkingSetSent());
    }
    if (this.chatHasHistory) return Promise.resolve();
    if (!this.ocrData || !this.aiSuggestedStrategies?.length) {
      this.workingSetDeferred = true;
      return Promise.resolve();
    }
    this.workingSetDeferred = false;

    const context = this.buildDiscoveryContext();
    const fingerprint = this.workingSetFingerprint(context);
    const key = this.workingSetKey();
    const inFlight = workingSetSends.get(key);
    if (inFlight && inFlight.chatId === this.chatId && inFlight.fingerprint === fingerprint) {
      return this.adoptWorkingSetSend(inFlight);
    }
    let saved: any = null;
    try {
      saved = JSON.parse(localStorage.getItem(key) || 'null');
    } catch {
      saved = null;
    }
    if (saved && saved.chatId === this.chatId && saved.fingerprint === fingerprint) {
      return Promise.resolve();
    }
    // The latest working set in the history is the list the chat saw. Same list: send nothing.
    // A set with no readable list is unknown, so nothing is sent.
    if (this.historyWorkingSet && this.historyWorkingSet.fingerprint !== null
      && this.historyWorkingSet.fingerprint === fingerprint) {
      return Promise.resolve();
    }
    if (this.historyWorkingSet && this.historyWorkingSet.fingerprint === null) {
      return Promise.resolve();
    }

    const chatId = this.chatId;
    const message: ChatMessage = { sender: 'ai', text: '', pending: true };
    const entry: WorkingSetSend = {
      chatId,
      fingerprint,
      message,
      promise: this.streamWorkingSetIntoChat(context, message)
        .then(sent => {
          // The fingerprint is saved also when this instance was destroyed during the stream.
          // A send that a Restart replaced does not save, because its chat is not the current chat.
          if (sent && workingSetSends.get(key) === entry) {
            localStorage.setItem(key, JSON.stringify({ chatId, fingerprint }));
          }
        })
        .catch(error => console.error('Working set send failed', error))
        .finally(() => {
          // A newer send for the same key stays in the map.
          if (workingSetSends.get(key) === entry) workingSetSends.delete(key);
        })
    };
    workingSetSends.set(key, entry);
    return entry.promise;
  }

  /**
   * Shows the bubble of a send that another instance started, and sends nothing.
   * The instance that started the send streams into the bubble. This one follows its end.
   */
  private adoptWorkingSetSend(entry: WorkingSetSend): Promise<void> {
    if (!this.messages.includes(entry.message)) this.messages.push(entry.message);
    this.messagesChange.emit(this.messages);
    this.isStreaming = true;
    return entry.promise.then(() => {
      if (this.destroyed) return;
      this.isStreaming = false;
      this.messagesChange.emit(this.messages);
    });
  }

  /**
   * Resolves when the working-set send for this chat has settled. It resolves at once when none is in flight.
   * It reads the shared map, so it works also when this instance adopted the send. It never rejects.
   * Await it before a prompt that must not meet the summary stream, such as End Conversation.
   */
  pendingSummary(): Promise<void> {
    this.exitStarted = true;
    const settled = () => {
      const entry = this.plan?.id ? workingSetSends.get(this.workingSetKey()) : undefined;
      if (!entry || entry.chatId !== this.chatId) return undefined;
      return entry.promise.then(() => undefined, () => undefined);
    };
    // A resumed chat can start the summary stream when its history load ends. Wait for the load first.
    if (this.historyLoad) return this.historyLoad.then(settled);
    return settled() ?? Promise.resolve();
  }

  /** Emits the messages unless this instance was destroyed. The shared bubbles are still updated. */
  private emitMessages(): void {
    if (!this.destroyed) this.messagesChange.emit(this.messages);
  }

  /**
   * Sends the working set and streams the reply into the given 'Summarizing…' AI bubble.
   * The working set itself is not shown as a user bubble.
   * Unlike triggerBackgroundOCRChat this does not use the page-wide loading overlay.
   * Resolves true when the stream closed normally. It makes one attempt only.
   */
  private async streamWorkingSetIntoChat(context: any, aiMsg: ChatMessage): Promise<boolean> {
    this.messages.push(aiMsg);
    this.messagesChange.emit(this.messages);
    this.isStreaming = true;

    const chatId = this.chatId;
    const result = await streamChatOnce({
      url: this.chatApiUrl,
      body: { aiAssistantId: this.aiAssistantId, chatId, prompt: `${JSON.stringify(context)}` },
      timeoutMs: RapQuestionnaireComponent.CHAT_TIMEOUT_MS,
      streamFn: this.streamFn,
      onData: (data) => {
        aiMsg.pending = false;
        aiMsg.text = data;
        this.emitMessages();
      }
    });

    // streamChatOnce always settles, so these lines always run.
    this.isStreaming = false;
    aiMsg.pending = false;

    if (result.outcome === 'closed') {
      if (aiMsg.text) {
        aiMsg.showCopy = true;
        this.attachMessageId(aiMsg, chatId);
      }
      this.emitMessages();
      return true;
    }

    console.error(`Working set stream ended as: ${result.outcome}`);
    // A partial reply stays. With no text at all, say so rather than leave the bubble empty.
    if (!aiMsg.text) {
      aiMsg.text = 'The summary did not come through. You can still ask a question below.';
      aiMsg.error = true;
    }
    this.emitMessages();
    return false;
  }

  /** Fetches the id of the latest answer so it can be rated. Non-critical: a failure only hides the rating buttons. */
  private attachMessageId(message: ChatMessage, chatId: number): void {
    this.knolliChatService.getLatestMessageId(chatId).subscribe({
      next: (res) => {
        // API returns the latest message id — adjust field name if needed
        const msgId = res?.data?.messageId;
        if (msgId) {
          message.messageId = msgId;
          this.emitMessages();
        }
      },
      error: () => {} // non-critical, rating just won't show
    });
  }

  getWelcomeMessage(): Promise<void> {
  return new Promise((resolve) => {
    this.loadingService.triggerLoadingEvent(true);
    this.knolliChatService.getWelcomeMessage(this.aiAssistantId).subscribe({
      next: (response) => {
        if (response?.description) {
          this.messages.push({ sender: 'ai', text: response.description });
        } else {
          this.messages.push({ sender: 'ai', text: '' });
        }

        this.messagesChange.emit(this.messages);
        resolve(); 
      },
      error: () => resolve(),
      complete: () => this.loadingService.triggerLoadingEvent(false)
    });
  });
}

  /** Sends the text in the input box. */
  async sendPrompt() {
    if (!this.chatId) {
      console.error("Chat ID not ready yet");
      return;
    }

    const prompt = this.userInput.trim();
    // While the history loads, the typed text stays in the box.
    if (!prompt || this.isStreaming || this.historyLoad) return;

    this.userInput = '';
    await this.sendMessage(prompt);
  }

  /** True while the working-set summary of the current chat is in flight. Sync form of pendingSummary(). */
  get summaryPending(): boolean {
    const entry = this.plan?.id ? workingSetSends.get(this.workingSetKey()) : undefined;
    return !!entry && entry.chatId === this.chatId;
  }

  /** True when Find More must not send: no chat, a reply streams, the history loads, or the summary is pending. */
  get quickActionsDisabled(): boolean {
    return !this.chatId || this.isStreaming || !!this.historyLoad || this.summaryPending;
  }

  /** Asks the chat for more strategies. */
  findMoreStrategies() {
    if (this.quickActionsDisabled) return Promise.resolve();
    return this.sendMessage(FIND_MORE_PROMPT);
  }

  /**
   * Sends one prompt to the chat and streams the reply into a new AI bubble.
   * The typed question and Find More both use it.
   * Resolves true when the stream closed normally.
   */
  private async sendMessage(prompt: string): Promise<boolean> {
    if (!this.chatId) {
      console.error("Chat ID not ready yet");
      return false;
    }
    if (!prompt || this.isStreaming) return false;

    this.isLoading = true;

    // Add user message
    this.messages.push({ sender: 'user', text: prompt, showCopy: false, copied: false });
    this.messagesChange.emit(this.messages);
    trackPendoEvent('Discovery Chat Message Sent', { message_index: this.messages.length });
    this.scrollToBottom();

    // Add placeholder AI message
    this.messages.push({ sender: 'ai', text: '' });
    const aiMsgIndex = this.messages.length - 1; // ✅ TRACK INDEX
    this.messagesChange.emit(this.messages);

    /** Track Discovery utilization */

    if (this.messages.length >= 4 && !this.engagementTracked && !this.questionnaireUtilization) {
        this.engagementTracked = true;
        const payload = { documentId: this.documentId, utilization: true};
        this.taxPlanningService.trackQuestionnaireUtilization(this.org,this.user, payload).subscribe();
    }

    this.isStreaming = true;

    const body = {
      aiAssistantId: this.aiAssistantId,
      chatId: this.chatId,
      prompt: prompt
    };

    const result = await streamChatOnce({
      url: this.chatApiUrl,
      body,
      timeoutMs: RapQuestionnaireComponent.CHAT_TIMEOUT_MS,
      streamFn: this.streamFn,
      onData: (data) => {
        this.isLoading = false;
        this.aiResponse = data;
        // Update last AI message
        this.messages[aiMsgIndex].text = data;
        this.messagesChange.emit(this.messages);
      }
    });

    // streamChatOnce always settles, so these two lines always run. Leaving
    // isStreaming on locks the send button for the rest of the session.
    this.isStreaming = false;
    this.isLoading = false;

    if (result.outcome === 'closed') {
      this.messages[aiMsgIndex].showCopy = true;
      // Fetch the message ID for this response so it can be rated
      this.attachMessageId(this.messages[aiMsgIndex], this.chatId);
    } else {
      console.error(`Chat prompt stream ended as: ${result.outcome}`);
      // Tell the user the answer failed. An empty bubble looks like the reply
      // is still on its way.
      if (this.messages[aiMsgIndex] && !this.messages[aiMsgIndex].text) {
        this.messages[aiMsgIndex].text = 'That answer did not come through. Please send your message again.';
        this.messagesChange.emit(this.messages);
      }
    }

    this.focusChatInput();
    return result.outcome === 'closed';
  }

  scrollToBottom() {
    setTimeout(() => {
      const el = this.messagesContainer.nativeElement;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }


  /**
   * Streams a background prompt to the chat service and returns the JSON the
   * answer carries.
   *
   * Warning: this method drives the global loading overlay, which blocks the
   * whole page. Every exit path must clear the overlay. A path that leaves it
   * on freezes the app (868kuzmap).
   *
   * This method never rejects. On any failure it resolves null, so the caller
   * continues without the chat data instead of stopping.
   */
  async triggerBackgroundOCRChat(systemPrompt: any, returnResponse: boolean = false): Promise< any | null> {
    if (!systemPrompt || !this.chatId) return null;

    this.loadingService.triggerLoadingEvent(true);

    const body = {
      aiAssistantId: this.aiAssistantId,
      chatId: this.chatId,
      prompt: `${JSON.stringify(systemPrompt)}`
    };

    const result = await streamChatOnce({
      url: this.chatApiUrl,
      body,
      timeoutMs: RapQuestionnaireComponent.CHAT_TIMEOUT_MS,
      streamFn: this.streamFn
    });

    // streamChatOnce always settles, so this line always runs. No branch above
    // it may return early, or the overlay stays on and the page freezes.
    this.loadingService.triggerLoadingEvent(false);

    if (result.outcome !== 'closed') {
      console.error(`Background OCR chat ended as: ${result.outcome}`);
      return null;
    }

    return returnResponse ? parseChatJson(result.data) : null;
  }

/**
 * Loads the history of a resumed chat. The returned promise settles when the load ends and never rejects.
 * Until then ensureWorkingSetSent waits. The latest working set in the history decides the send.
 * A failed load stops the send.
 */
loadOldMessages(chatId: number): Promise<void> {
  let settle!: () => void;
  const load = new Promise<void>(resolve => { settle = resolve; });
  this.historyLoad = load;
  // A chat never uses the working-set state of an earlier chat.
  this.historyWorkingSet = undefined;
  const done = () => {
    if (this.historyLoad === load) this.historyLoad = undefined;
    settle();
  };
  this.taxPlanningService.getChatHistory(this.org,this.user,chatId).subscribe({
    next: (res: any) => {
      // A Restart replaced this chat while the load ran. Its history is not for the new chat.
      if (chatId !== this.chatId) return;
      if (!res?.data?.generatedMessage) return;
      // The list is newest first. Reverse it so the oldest entry comes first.
      const historyArray = [...res.data.generatedMessage].reverse();
      // Remember the latest working set in the history. A chat with none, such as a legacy chat that
      // holds the raw OCR prompt, gets the list. A chat whose latest set holds another list gets it too.
      const workingSets = historyArray
        .map((item: any) => parseWorkingSetPrompt(item.userPrompt))
        .filter((set: any) => !!set);
      this.historyWorkingSet = workingSets.length
        ? { fingerprint: this.historyFingerprint(workingSets[workingSets.length - 1]) }
        : undefined;

      const formattedMessages: ChatMessage[] = [];

      historyArray.forEach((item: any, index: number) => {
        const isWorkingSet = isWorkingSetPrompt(item.userPrompt);

        // The oldest entry is the context prompt the backend seeds, and nothing marks it.
        // It stays dropped by its position. A working set has a reply to show, so it stays.
        if (index === 0 && !isWorkingSet) {
          return;
        }

        const prompt = item.userPrompt?.trim()?.replace(/^"|"$/g, '');
        if (prompt?.toLowerCase() === 'end conversation') {
          return;
        }

        // The working set is context for the assistant. Only its reply is shown.
        if (!isWorkingSet && item.userPrompt && item.promptType === 'User_Prompt') {
          formattedMessages.push({
            sender: 'user',
            text: item.userPrompt
          });
        }

        if (item.aiMessage) {
          formattedMessages.push({
            sender: 'ai',
            text: item.aiMessage,
            showCopy: true,
            copied: false,
            messageId: item.aiMessageId,
            userRating: item.userRating
          });
        }

      });

      // History is older than what is on screen, such as a bubble that is streaming now.
      this.messages = [...formattedMessages, ...this.messages];
      this.messagesChange.emit(this.messages);

      setTimeout(() => this.scrollToBottom(), 100);

    },
    error: err => {
      if (chatId === this.chatId) this.chatHasHistory = true;
      this.loadingService.triggerLoadingEvent(false);
      done();
    },
    complete: () => {
      this.loadingService.triggerLoadingEvent(false);
      done();
    }
  });
  return load;
}

 goToNextSection(section: string) {
    this.nextClicked.emit(section);
  }

  async showAiQuestionnarieSection(clicked = true){
    if (clicked) trackPendoEvent('Discovery Find More Clicked');
     if ((!this.messages || this.messages.length === 0) && this.chatId) {
      this.loadOldMessages(this.chatId);
    }
    if (!this.chatId || Number(this.chatId === 0)) {
      this.initializeChatId();
    }
    // A new chat gets the list when it is created. An existing chat gets it here.
    this.ensureWorkingSetSent();
    trackPendoEvent('Discovery Chat Viewed');
    // The parent already starts Discovery on track 1. Emitting then would change its
    // binding during its own change detection (NG0100), so emit only on a real change.
    if (this.questionnaireTrack != 1) {
      this.sectionChange.emit(1); // stay in Chat
    }
    this.updateQuestionnarieStep('AI_CHAT');

  }

  async restartQuestionnaire(){
    trackPendoEvent('Discovery Chat Restarted');
    this.chatId = 0;
    this.messages =[];
    this.chatHasHistory = false;
    this.historyWorkingSet = undefined;
    this.historyLoad = undefined;
    this.exitStarted = false;
    localStorage.removeItem(`chatId_${this.plan.id}`);
    localStorage.removeItem(`chatId_${this.plan.id}_workingSet`);
    if (this.sessionId) {
      // RAP edit mode saves the chat under a session key
      localStorage.removeItem(`chatId_${this.plan.id}_${this.sessionId}`);
      localStorage.removeItem(`chatId_${this.plan.id}_${this.sessionId}_workingSet`);
    }
    if (!this.chatId || Number(this.chatId === 0)) {
      this.initializeChatId();
    }
  }

focusChatInput() {
  setTimeout(() => {
    if (this.feedbackIndex !== null) return;

    const active = document.activeElement as HTMLElement;
    if (active?.closest('.feedback-panel')) return;
    if (active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA') return; // ← catches any input

    const el = this.chatInput?.nativeElement;
    if (el && active !== el) {
      el.focus();
    }
  }, 100);
}

  updateQuestionnarieStep(step: 'AI_CHAT' | 'TOP_5_STRATEGIES') {
  // RAP sandbox: Do NOT call backend - questionnaire_track stays in session only.
  // Normal flow: persist to database.
  if (!this.isRapEditMode) {
    const finalData = { questionnaireTrack: step };
    this.taxPlanningService
      .updateQuestionnarieStep(this.org, this.user, this.documentId, finalData)
      .subscribe(response => {
        console.log('Updated questionnaireTrack:', step);
      });
  }
 }

 feedbackIndex: number | null = null;
 feedbackText = '';

 async onCopyMessage(index: number) {
  const msg = this.messages[index];
  if (!msg?.text) return;

  let textToCopy = this.getMessagePlainText(index);
  if (!textToCopy) {
    textToCopy = this.sanitizeMessageForCopy(msg.text);
  }

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(textToCopy);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    this.copiedIndex = index;
    setTimeout(() => {
      if (this.copiedIndex === index) {
        this.copiedIndex = null;
      }
    }, 1500);
  } catch (error) {
    console.error('Failed to copy message:', error);
  }
 }

 copyMessage(index: number) {
  this.onCopyMessage(index);
 }

 private getMessagePlainText(index: number): string {
  const messageRows = document.querySelectorAll('.messages .message-row.ai');
  const messageEl = messageRows[index]?.querySelector('.bubble') as HTMLElement | null;
  if (!messageEl) return '';

  const clonedMessageEl = messageEl.cloneNode(true) as HTMLElement;

  const actionArea = clonedMessageEl.querySelector('.rating-actions');
  const feedbackPanel = clonedMessageEl.querySelector('.feedback-panel');

  if (actionArea) {
    actionArea.remove();
  }
  if (feedbackPanel) {
    feedbackPanel.remove();
  }

  const text = (clonedMessageEl.innerText || clonedMessageEl.textContent || '')
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

  return text;
 }

 private sanitizeMessageForCopy(rawText: string): string {
  return rawText
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
 }

 rateMessage(index: number, rating: 1 | -1) {
  const msg = this.messages[index];
  if (!msg?.messageId) return;

  const isToggleOff = msg.userRating === rating;
  const newRating = isToggleOff ? null : rating;
  const apiValue = isToggleOff ? 0 : rating;

  this.messages[index] = { ...msg, userRating: newRating };
  this.messagesChange.emit(this.messages);

  if (rating === -1 && !isToggleOff) {
    this.feedbackIndex = index;
    this.feedbackText = '';
    this.knolliChatService.rateMessage(msg.messageId, -1).subscribe();
    if (index === this.messages.length - 1) {
      setTimeout(() => {
        const container = this.messagesContainer?.nativeElement;
        if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }, 50);
    }
  } else {
    if (this.feedbackIndex === index) this.closeFeedback();
    this.knolliChatService.rateMessage(msg.messageId, apiValue as any).subscribe();
  }
}

 closeFeedback() {
  this.feedbackIndex = null;
  this.feedbackText = '';
  this.focusChatInput(); 
}

 submitFeedback(index: number) {
  const msg = this.messages[index];
  if (!msg?.messageId) return;
  this.knolliChatService.rateMessage(msg.messageId, -1, this.feedbackText.trim() || undefined).subscribe();
  this.closeFeedback();

}

}
